import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { ArrowLeft, Edit, Trash2, Calendar, MapPin, Youtube, ExternalLink, Play, Pause, Settings, DollarSign, Hash } from 'lucide-react'
import { useToast } from '../../hooks/useToast'
import Toast from '../../components/ui/Toast'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import leiloesService from '../../services/leiloesService'
import { lotesService } from '../../services/lotesService'

export default function DetalhesLeilao() {
  const { id } = useParams()
  const { toasts, success, error, hideToast } = useToast()
  const { organizadorAtivo } = useOrganizador()
  const [leilao, setLeilao] = useState(null)
  const [loading, setLoading] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(null) // { lotesCount, loadingDetails }

  const organizadorId = organizadorAtivo?.id

  useEffect(() => {
    if (organizadorId && id) {
      loadLeilao()
    }
  }, [organizadorId, id])

  const loadLeilao = async () => {
    try {
      setLoading(true)
      const data = await leiloesService.getById(organizadorId, id)
      setLeilao(data)
    } catch (err) {
      console.error('Erro ao carregar leilão:', err)
      error('Erro ao carregar dados do leilão')
    } finally {
      setLoading(false)
    }
  }

  const openDeleteDialog = async () => {
    setConfirmDelete({ lotesCount: null, loadingDetails: true })
    try {
      const lotes = await lotesService.listarLotesPorLeilao(organizadorId, id)
      setConfirmDelete({ lotesCount: lotes.length, loadingDetails: false })
    } catch {
      setConfirmDelete({ lotesCount: 0, loadingDetails: false })
    }
  }

  const handleDelete = async () => {
    try {
      await leiloesService.delete(organizadorId, id)
      success('Leilão excluído com sucesso!')
      setTimeout(() => { window.location.href = '/leiloes' }, 1500)
    } catch (err) {
      console.error('Erro ao excluir leilão:', err)
      const errorMessage = err.response?.data?.detail || 'Erro ao excluir leilão'
      error(errorMessage)
    }
    setConfirmDelete(null)
  }

  const toggleStatus = async () => {
    try {
      const novoStatus = leilao.status === 'on' ? 'off' : 'on'
      await leiloesService.update(organizadorId, id, { status: novoStatus })
      success(`Leilão ${novoStatus === 'on' ? 'ativado' : 'desativado'} com sucesso!`)
      setLeilao({ ...leilao, status: novoStatus })
    } catch (err) {
      console.error('Erro ao alterar status:', err)
      error('Erro ao alterar status do leilão')
    }
  }

  const formatDate = (dateString, timeString) => {
    if (!dateString) return '-'
    
    const date = new Date(dateString)
    let formattedDate = date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
    
    if (timeString) {
      formattedDate += ` às ${timeString}`
    }
    
    return formattedDate
  }

  const getTipoLabel = (tipo) => {
    const tipos = {
      'gado_corte': 'Gado de Corte',
      'gado_leite': 'Gado de Leite',
      'gado_elite': 'Gado de Elite',
      'cavalo': 'Cavalo',
      'beneficente': 'Beneficente'
    }
    return tipos[tipo] || tipo
  }

  const getTipoColor = (tipo) => {
    const cores = {
      'gado_corte': 'bg-blue-100 text-blue-800',
      'gado_leite': 'bg-green-100 text-green-800',
      'gado_elite': 'bg-purple-100 text-purple-800',
      'cavalo': 'bg-amber-100 text-amber-800',
      'beneficente': 'bg-pink-100 text-pink-800'
    }
    return cores[tipo] || 'bg-gray-100 text-gray-800'
  }

  const getFluxoLabel = (fluxo) => {
    const fluxos = {
      'primeira_parcela': 'Comissão na Primeira Parcela',
      'gta_nota': 'GTA + NOTA',
      'separada': 'Comissão Separada',
      'parcelada': 'Comissão Parcelada'
    }
    return fluxos[fluxo] || fluxo
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rematei-blue"></div>
      </div>
    )
  }

  if (!leilao) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Leilão não encontrado
        </h3>
        <button
          onClick={() => window.location.href = '/leiloes'}
          className="btn-primary"
        >
          Voltar para Leilões
        </button>
      </div>
    )
  }

  return (
    <>
      {/* Toasts */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}

      {/* Modal de Confirmação */}
      {(() => {
        const lotesCount = confirmDelete?.lotesCount ?? null
        const loadingDetails = confirmDelete?.loadingDetails ?? false
        const temLotes = lotesCount !== null && lotesCount > 0
        const local = [leilao.cidade, leilao.uf].filter(Boolean).join(', ') || leilao.local || '-'
        const details = loadingDetails ? [] : [
          { label: 'Leilão', value: leilao.nome },
          { label: 'Data', value: leilao.data_leilao ? formatDate(leilao.data_leilao, leilao.hora_inicio) : '-' },
          { label: 'Local', value: local },
          { label: 'Status', value: leilao.status === 'on' ? 'Ativo' : 'Inativo' },
          { label: 'Lotes', value: lotesCount !== null ? `${lotesCount} lote(s)` : '-', danger: temLotes },
        ]
        return (
          <ConfirmDialog
            isOpen={!!confirmDelete}
            onClose={() => setConfirmDelete(null)}
            onConfirm={handleDelete}
            title={temLotes ? 'Não é possível excluir' : 'Excluir Leilão'}
            message={
              temLotes
                ? `O leilão "${leilao.nome}" possui ${lotesCount} lote(s). Remova todos os lotes antes de excluir o leilão.`
                : 'Tem certeza que deseja excluir este leilão? Esta ação não pode ser desfeita.'
            }
            details={details}
            blocked={temLotes}
            loading={loadingDetails}
            confirmText="Excluir"
            cancelText="Cancelar"
            type="danger"
          />
        )
      })()}

      <div className="">
        {/* Header Padrão */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => window.location.href = '/leiloes'}
                className="p-2 rounded-xl text-white hover:bg-white/20 transition-all duration-200 flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-white truncate">{leilao.nome}</h1>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="bg-white/20 text-white px-2 py-0.5 text-xs font-medium rounded-full">
                    {getTipoLabel(leilao.tipo_leilao)}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    leilao.status === 'on'
                      ? 'bg-green-500/20 text-green-100'
                      : 'bg-gray-500/20 text-gray-200'
                  }`}>
                    {leilao.status === 'on' ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => window.location.href = `/catalogo-leilao/${id}`}
                className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all font-medium text-sm"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Catálogo
              </button>
              <button
                onClick={() => window.location.href = `/ordem-entrada/${id}`}
                className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all font-medium text-sm"
              >
                <Hash className="w-3.5 h-3.5" />
                Ordem
              </button>
              <button
                onClick={toggleStatus}
                className="bg-white/10 hover:bg-white/20 text-white p-1.5 rounded-lg transition-all"
                title={leilao.status === 'on' ? 'Desativar' : 'Ativar'}
              >
                {leilao.status === 'on' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              <button
                onClick={() => window.location.href = `/leiloes/${id}/editar`}
                className="bg-white hover:bg-gray-100 text-blue-600 px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all shadow-lg font-medium text-sm"
              >
                <Edit className="w-3.5 h-3.5" />
                Editar
              </button>
              <button
                onClick={openDeleteDialog}
                className="bg-red-500/10 hover:bg-red-500/20 text-red-200 hover:text-white p-1.5 rounded-lg transition-all"
                title="Excluir Leilão"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-0 sm:px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informações Básicas */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Informações Básicas
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Data de Início
                  </label>
                  <div className="flex items-center text-gray-900">
                    <Calendar className="w-4 h-4 mr-2" />
                    {formatDate(leilao.data_leilao, leilao.hora_inicio)}
                  </div>
                </div>

                {leilao.data_fim && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Data de Fim
                    </label>
                    <div className="flex items-center text-gray-900">
                      <Calendar className="w-4 h-4 mr-2" />
                      {formatDate(leilao.data_fim, leilao.hora_fim)}
                    </div>
                  </div>
                )}

                {(leilao.cidade || leilao.uf) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Localização
                    </label>
                    <div className="flex items-center text-gray-900">
                      <MapPin className="w-4 h-4 mr-2" />
                      {leilao.cidade}{leilao.cidade && leilao.uf && ', '}{leilao.uf}
                    </div>
                  </div>
                )}

                {leilao.local && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Local
                    </label>
                    <p className="text-gray-900">{leilao.local}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Mídia */}
            {(leilao.link_transmissao || leilao.banner_url || leilao.catalogo_link) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Youtube className="w-5 h-5 mr-2" />
                  Mídia
                </h2>
                
                <div className="space-y-4">
                  {leilao.link_transmissao && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        Link da Transmissão
                      </label>
                      <a
                        href={leilao.link_transmissao}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700"
                      >
                        <Youtube className="w-4 h-4 mr-2" />
                        Assistir no YouTube
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </div>
                  )}

                  {leilao.banner_url && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        Banner do Leilão
                      </label>
                      <div className="w-full max-w-md">
                        <img
                          src={leilao.banner_url.startsWith('http') ? leilao.banner_url : `${(import.meta.env.VITE_API_URL || 'http://localhost:8001/').replace(/\/$/, '')}${leilao.banner_url}`}
                          alt="Banner do leilão"
                          className="w-full h-32 object-contain rounded-lg border border-gray-200 shadow-sm bg-gray-50"
                          onError={(e) => {
                            console.error('Erro ao carregar banner:', e.target.src)
                            e.target.style.display = 'none'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {leilao.catalogo_link && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-2">
                        Catálogo
                      </label>
                      <a
                        href={leilao.catalogo_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700"
                      >
                        Ver Catálogo
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Configurações Financeiras */}
            {leilao.fluxo_comissao && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Configurações Financeiras
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Fluxo de Comissão
                    </label>
                    <p className="text-gray-900">{getFluxoLabel(leilao.fluxo_comissao)}</p>
                  </div>

                  {leilao.fluxo_comissao === 'gta_nota' && (
                    <>
                      {leilao.valor_gta > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Valor GTA
                          </label>
                          <p className="text-gray-900">
                            R$ {parseFloat(leilao.valor_gta).toFixed(2)} por animal
                          </p>
                        </div>
                      )}

                      {leilao.valor_nota > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Valor NOTA
                          </label>
                          <p className="text-gray-900">
                            R$ {parseFloat(leilao.valor_nota).toFixed(2)} por vendedor
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Informações do Sistema */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Informações do Sistema
              </h2>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">ID:</span>
                  <span className="text-gray-900">#{leilao.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Criado em:</span>
                  <span className="text-gray-900">
                    {formatDate(leilao.created_at)}
                  </span>
                </div>
                {leilao.updated_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Atualizado em:</span>
                    <span className="text-gray-900">
                      {formatDate(leilao.updated_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  )
}